/**
 * PWA Update Coordinator (#517, Release 1)
 *
 * The single owner of "is there a newer deploy, and how do we get the user onto
 * it without intervention". Replaces the contention between vite-plugin-pwa
 * autoUpdate, versionManager's own reload, and the (now deleted) update-prompt
 * path.
 *
 * Detection is by BUILD ID, not service-worker byte-diff: CDN builds can ship a
 * byte-identical sw.js while the app bundle changed (globPatterns: [] —
 * vite.config.ts), so a SW-lifecycle-only check misses real updates (#517 D5).
 * We snapshot the server `/version.json` version seen at THIS load; a later
 * foreground check that sees a different server version means a new deploy.
 *
 * Triggers (fixes iOS, where controllerchange/activated reload is unreliable and
 * hourly timers die when backgrounded — #517 Q5): on construction, and on
 * `visibilitychange`/`focus`/`pageshow`/`online`, debounced.
 *
 * Reload is guarded by a PERSISTENT per-target localStorage guard (not
 * sessionStorage, which versionManager used to wipe — #517 D3): at most
 * MAX_RELOADS attempts per target build inside a window, cleared once the
 * running load matches the target.
 *
 * Release 1 keeps the SW's unconditional skipWaiting (existing installs still
 * update); Release 2 will move to coordinated waiting-worker activation.
 */

const CURRENT_BUILD_KEY = "pwa_current_build";
const RELOAD_GUARD_KEY = "pwa_reload_guard"; // localStorage: {target, attempts, firstAttempt}
const VERSION_FETCH_TIMEOUT_MS = 8_000;
const FOREGROUND_DEBOUNCE_MS = 4_000;
const MAX_RELOADS = 2;
const RELOAD_WINDOW_MS = 60_000;

interface ReloadGuard {
  target: string;
  attempts: number;
  firstAttempt: number;
}

interface VersionFile {
  version?: string;
}

function readGuard(): ReloadGuard | null {
  try {
    const raw = localStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) return null;
    const g = JSON.parse(raw) as ReloadGuard;
    if (typeof g.target === "string" && typeof g.attempts === "number") return g;
    return null;
  } catch {
    return null;
  }
}

function writeGuard(g: ReloadGuard): void {
  try {
    localStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(g));
  } catch {
    /* private mode / quota */
  }
}

function clearGuard(): void {
  try {
    localStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * May we reload toward `target` now? Bounds reloads per target to MAX_RELOADS
 * inside RELOAD_WINDOW_MS so a broken build / flapping CDN can't reload-storm.
 * Returns true and records the attempt when allowed.
 */
export function canReloadTo(target: string, now: number): boolean {
  const g = readGuard();
  if (!g || g.target !== target) {
    writeGuard({ target, attempts: 1, firstAttempt: now });
    return true;
  }
  // Same target: window expired → reset and allow.
  if (now - g.firstAttempt > RELOAD_WINDOW_MS) {
    writeGuard({ target, attempts: 1, firstAttempt: now });
    return true;
  }
  if (g.attempts >= MAX_RELOADS) return false;
  writeGuard({ ...g, attempts: g.attempts + 1 });
  return true;
}

async function fetchServerVersion(timeoutMs: number): Promise<string | null> {
  try {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        signal: controller.signal,
      });
      if (!res.ok) return null;
      const data = (await res.json()) as VersionFile;
      return typeof data.version === "string" ? data.version : null;
    } finally {
      clearTimeout(t);
    }
  } catch {
    return null; // offline / timeout / parse — treat as "no info", never wipe
  }
}

export interface PwaUpdateCoordinatorDeps {
  /** Pull the latest SW from the network (registration.update()); swallow iOS InvalidStateError upstream. */
  updateRegistration: () => Promise<void>;
  /** True when a reload would clobber in-progress user work (composer/upload). */
  isBusy?: () => boolean;
  /** Perform the reload. Default: location.reload(). */
  reload?: () => void;
  /**
   * Activate a waiting service worker (postMessage SKIP_WAITING). Returns true if
   * a waiting worker existed and was told to activate. When it does, the page's
   * `controllerchange` listener performs the reload — we do NOT also call reload()
   * directly (that would double-reload). Returns false when there is no waiting
   * worker (e.g. byte-identical SW, or iOS where it never installed), so the
   * coordinator falls back to a plain build-ID nav reload.
   */
  activateWaitingWorker?: () => Promise<boolean>;
}

export class PwaUpdateCoordinator {
  private deps: PwaUpdateCoordinatorDeps;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private checking = false;
  private listenersBound = false;

  constructor(deps: PwaUpdateCoordinatorDeps) {
    this.deps = deps;
  }

  /** Snapshot the build this load is running, and confirm-clear the reload guard. */
  async start(): Promise<void> {
    const serverVersion = await fetchServerVersion(VERSION_FETCH_TIMEOUT_MS);
    if (serverVersion) {
      const stored = safeGet(CURRENT_BUILD_KEY);
      // We are now running whatever the server currently serves: record it, and
      // if a prior reload targeted this build, the guard's job is done.
      if (stored !== serverVersion) safeSet(CURRENT_BUILD_KEY, serverVersion);
      const g = readGuard();
      if (g && g.target === serverVersion) clearGuard();
    }
    this.bindForegroundTriggers();
  }

  private bindForegroundTriggers(): void {
    if (this.listenersBound || typeof window === "undefined") return;
    this.listenersBound = true;
    const trigger = () => this.scheduleCheck();
    // iOS: these fire when hourly timers are dead (backgrounded). pageshow also
    // fires on bfcache restore. online catches reconnect after a deploy.
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") trigger();
    });
    window.addEventListener("focus", trigger);
    window.addEventListener("pageshow", trigger);
    window.addEventListener("online", trigger);
  }

  private scheduleCheck(): void {
    if (this.debounceTimer) return; // already scheduled — debounce
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.checkForUpdate();
    }, FOREGROUND_DEBOUNCE_MS);
  }

  /** Compare server build vs the build this load started on; reload if newer. */
  async checkForUpdate(): Promise<void> {
    if (this.checking) return;
    this.checking = true;
    try {
      // Nudge the SW too (best-effort); the build-ID compare is the real signal.
      await this.deps.updateRegistration().catch(() => {});

      const serverVersion = await fetchServerVersion(VERSION_FETCH_TIMEOUT_MS);
      if (!serverVersion) return; // offline / unknown — do nothing, never wipe

      const runningBuild = safeGet(CURRENT_BUILD_KEY);
      if (!runningBuild) {
        // First time we got a version — record and stand down.
        safeSet(CURRENT_BUILD_KEY, serverVersion);
        return;
      }
      if (serverVersion === runningBuild) return; // up to date

      // New deploy detected.
      if (this.deps.isBusy?.()) return; // defer: don't clobber composer/upload
      if (!canReloadTo(serverVersion, Date.now())) return; // loop guard

      // Record the target so start() can confirm-clear the guard after the reload.
      safeSet(CURRENT_BUILD_KEY, serverVersion);

      // Prefer coordinated activation: tell the WAITING new SW to take over. Its
      // controllerchange fires the reload (wired in usePWA). Only if there is no
      // waiting worker (byte-identical SW / iOS never-installed) do we fall back
      // to a plain nav reload, which still picks up the new build over the network.
      if (this.deps.activateWaitingWorker) {
        const activated = await this.deps.activateWaitingWorker();
        if (activated) return; // controllerchange handler will reload
      }
      (this.deps.reload ?? (() => window.location.reload()))();
    } finally {
      this.checking = false;
    }
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
